import { useEffect, useState } from "react";
import { Link } from "react-router";
import { ErrorNotice, useAction } from "../components/ui";
import { api, type Dashboard, type Me, type MoneyByCurrency } from "../lib/api";
import { noticeEmail, noticeUrl } from "../lib/notice";
import { addDays, friendlyDate, todayInLondon } from "../lib/dates";
import { formatDay, JOB_STATUS_LABEL } from "../lib/format";
import { daysSince } from "./PipelineBoard";

const pad = (n: number) => String(n).padStart(2, "0");
const interviewWhen = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" });

/** "£20,000 + €9,000"; currencies are listed separately, never converted or added. */
export function formatMoneyByCurrency(m: MoneyByCurrency): string {
  const parts = Object.entries(m)
    .filter(([, v]) => v)
    .map(([currency, amount]) => new Intl.NumberFormat("en-GB", { style: "currency", currency, maximumFractionDigits: 0 }).format(amount!));
  return parts.length ? parts.join(" + ") : "—";
}

/** Below the reminders on Today: your jobs, who needs a nudge, the team, and recent wins. */
export function DeskSections({ me }: { me: Me }) {
  const [d, setD] = useState<Dashboard | null>(null);
  const load = useAction();
  const act = useAction();

  useEffect(() => {
    void load.run(async () => setD(await api.dashboard()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const noticeSent = (candidateId: string) =>
    void act.run(async () => {
      await api.updateCandidate(candidateId, { privacyNoticeSentOn: todayInLondon() });
      setD(await api.dashboard());
    });

  if (!d) return <ErrorNotice error={load.error} />;

  return (
    <>
      <section className="section" aria-labelledby="myjobs-h">
        <div className="section-head">
          <h2 id="myjobs-h" className="label">
            Your active jobs
          </h2>
          <span className="label figures">{pad(d.myJobs.length)}</span>
        </div>
        <ol className="index">
          {d.myJobs.map((j, i) => (
            <li key={j.id}>
              <span className="num" aria-hidden>
                {pad(i + 1)}
              </span>
              <span className="main">
                <span className="primary">
                  <Link to={`/jobs/${j.id}`}>{j.title}</Link>
                  <span className="secondary"> · {j.company.name}</span>
                </span>
                <span className="secondary">
                  <span className="figures">{j.inProcess}</span> in process
                  {j.placed > 0 && (
                    <>
                      {" · "}
                      <span className="figures">{j.placed}</span> placed
                    </>
                  )}
                  {" · moved "}
                  {daysSince(j.lastActivity) === "today" ? "today" : `${daysSince(j.lastActivity)} ago`}
                </span>
              </span>
              <span className="actions">{j.status !== "open" && <span className="status pending">{JOB_STATUS_LABEL[j.status]}</span>}</span>
            </li>
          ))}
          {d.myJobs.length === 0 && (
            <li>
              <span className="empty">No open jobs of yours. Create one from a client's page.</span>
            </li>
          )}
        </ol>
      </section>

      {d.interviews.length > 0 && (
        <section className="section" aria-labelledby="interviews-h">
          <div className="section-head">
            <h2 id="interviews-h" className="label">
              Interviews this week
            </h2>
            <span className="label figures">{pad(d.interviews.length)}</span>
          </div>
          <ol className="index">
            {d.interviews.map((iv, i) => (
              <li key={iv.id}>
                <span className="num" aria-hidden>
                  {pad(i + 1)}
                </span>
                <span className="main">
                  <Link className="primary" to={`/applications/${iv.applicationId}`}>
                    {iv.candidateName}
                  </Link>
                  <span className="secondary">
                    <span className="figures">{interviewWhen.format(new Date(iv.startsAt))}</span> · {iv.stageName} · {iv.job.title} at {iv.companyName}
                  </span>
                </span>
                <span className="actions" />
              </li>
            ))}
          </ol>
        </section>
      )}

      {d.stale.length > 0 && (
        <section className="section" aria-labelledby="stale-h">
          <div className="section-head">
            <h2 id="stale-h" className="label alert">
              Needs a nudge · 14+ days in one stage
            </h2>
            <span className="label figures">{pad(d.stale.length)}</span>
          </div>
          <ol className="index">
            {d.stale.map((s, i) => (
              <li key={s.applicationId}>
                <span className="num" aria-hidden>
                  {pad(i + 1)}
                </span>
                <span className="main">
                  <Link className="primary" to={`/applications/${s.applicationId}`}>
                    {s.candidateName}
                  </Link>
                  <span className="secondary">
                    {s.stageName} for <span className="figures late">{s.days} days</span> · {s.job.title}
                  </span>
                </span>
                <span className="actions" />
              </li>
            ))}
          </ol>
        </section>
      )}

      {d.privacyNotices.total > 0 && (
        <PrivacyNotices me={me} notices={d.privacyNotices} busy={act.pending} error={act.error} onSent={noticeSent} />
      )}

      <section className="section" aria-labelledby="team-h">
        <div className="section-head">
          <h2 id="team-h" className="label">
            Team this month
          </h2>
          <span className="label figures">{formatMoneyByCurrency(d.agencyFeesThisMonth)} in fees</span>
        </div>
        <div className="team-table">
          <table>
            <thead>
              <tr>
                <th className="label">Who</th>
                <th className="label num-col">Open jobs</th>
                <th className="label num-col">In process</th>
                <th className="label num-col">Chases due</th>
                <th className="label num-col">Placed</th>
                <th className="label num-col">Fees</th>
              </tr>
            </thead>
            <tbody>
              {d.team.map((t) => (
                <tr key={t.id}>
                  <td>
                    {t.name}
                    {t.isYou && <span className="label"> · you</span>}
                  </td>
                  <td className="figures num-col" data-label="Open jobs">{t.openJobs}</td>
                  <td className="figures num-col" data-label="In process">{t.inProcess}</td>
                  <td className={`figures num-col ${t.remindersDue ? "late" : ""}`} data-label="Chases due">{t.remindersDue}</td>
                  <td className="figures num-col" data-label="Placed">{t.placementsThisMonth}</td>
                  <td className="figures num-col" data-label="Fees">{formatMoneyByCurrency(t.feesThisMonth)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {d.recentPlacements.length > 0 && (
        <section className="section" aria-labelledby="wins-h">
          <div className="section-head">
            <h2 id="wins-h" className="label">
              Recent placements · last 30 days
            </h2>
          </div>
          <ol className="index">
            {d.recentPlacements.map((p, i) => (
              <li key={p.id}>
                <span className="num" aria-hidden>
                  {pad(i + 1)}
                </span>
                <span className="main">
                  <Link className="primary" to={`/applications/${p.applicationId}`}>
                    {p.candidateName}
                  </Link>
                  <span className="secondary">
                    {p.job.title} at {p.companyName} · {p.owner} · starts {formatDay(p.startDate)}
                  </span>
                </span>
                <span className="actions figures">{formatMoneyByCurrency({ [p.fee.currency]: p.fee.amount })}</span>
              </li>
            ))}
          </ol>
        </section>
      )}
    </>
  );
}

/**
 * Candidates owed a privacy notice. UK GDPR gives a month from getting someone's details (sooner
 * if you contact them first); the recruiter sends the agency's notice, then marks it here.
 */
function PrivacyNotices({
  me,
  notices,
  busy,
  error,
  onSent,
}: {
  me: Me;
  notices: Dashboard["privacyNotices"];
  busy: boolean;
  error: string | null;
  onSent: (candidateId: string) => void;
}) {
  const today = todayInLondon();
  // Overdue and due within a week stay in view; the rest fold away so Today isn't swamped.
  const weekOut = addDays(today, 7);
  const soon = notices.candidates.filter((c) => c.dueOn <= weekOut);
  const later = notices.candidates.filter((c) => c.dueOn > weekOut);
  return (
    <section className="section" aria-labelledby="notices-h">
      <div className="section-head">
        <h2 id="notices-h" className={`label ${notices.overdue ? "alert" : ""}`}>
          Privacy notices to send{notices.overdue ? ` · ${notices.overdue} overdue` : ""}
        </h2>
        <span className="label figures">{pad(notices.total)}</span>
      </div>
      <p className="hint">
        Your candidates with no privacy notice recorded. Tell people you hold their details within a month of adding them, or when you
        first contact them if that's sooner. Email them a link to <Link to={`/notice/${me.tenant.id}`}>your agency's notice</Link>, then mark it here.
      </p>
      <ErrorNotice error={error} />
      <NoticeList me={me} candidates={soon} today={today} busy={busy} onSent={onSent} />
      {soon.length === 0 && <p className="empty-line">Nothing due this week.</p>}
      {later.length > 0 && (
        <details className="earlier">
          <summary className="label">Due later · {later.length}</summary>
          <NoticeList me={me} candidates={later} today={today} busy={busy} onSent={onSent} />
        </details>
      )}
      {notices.total > notices.candidates.length && (
        <p className="hint">and {notices.total - notices.candidates.length} more. Mark these first; the oldest are listed.</p>
      )}
    </section>
  );
}

function NoticeList({
  me,
  candidates,
  today,
  busy,
  onSent,
}: {
  me: Me;
  candidates: Dashboard["privacyNotices"]["candidates"];
  today: string;
  busy: boolean;
  onSent: (candidateId: string) => void;
}) {
  if (candidates.length === 0) return null;
  return (
    <ol className="index">
      {candidates.map((c, i) => (
        <li key={c.id}>
          <span className="num" aria-hidden>
            {pad(i + 1)}
          </span>
          <span className="main">
            <Link className="primary" to={`/candidates/${c.id}`}>
              {c.fullName}
            </Link>
            <span className="secondary">
              {c.source && <>{c.source} · </>}
              {c.dueOn < today ? "was due " : "due "}
              <span className={`figures ${c.dueOn < today ? "late" : ""}`}>{friendlyDate(c.dueOn, today)}</span>
            </span>
          </span>
          <span className="actions">
            {c.email && (
              <a className="link-btn" href={noticeEmail({ fullName: c.fullName, email: c.email }, me.tenant.name, me.user.displayName ?? me.user.email, noticeUrl(me.tenant.id))}>
                Email notice
              </a>
            )}
            <button type="button" className="link-btn" disabled={busy} onClick={() => onSent(c.id)}>
              Sent today
            </button>
          </span>
        </li>
      ))}
    </ol>
  );
}
